/**
 * Purchase order store (S3.2)
 *
 * Zustand store for the PO list surface — filter state (persisted),
 * pagination echo, the store-first list echo the mutation hooks update —
 * plus the client-side vendor-checkpoint log.
 *
 * Filter contract (holistic plan §S3.2 — GenericFilterPopover status /
 * approval / vendor):
 * - `search` — poNumber / vendorName / sourceSoNumber
 * - `status` — draft|sent|vendor_confirmed|shipped|received ('all' = none)
 * - `approvalStatus` — the >NT$200k gate state ('all' = none)
 * - `vendorIds` — multi-select; >1 unions client-side over a mock-scale
 *   fetch (the mock list endpoint accepts a single vendorId only)
 *
 * Checkpoint log: the mock API persists checkpoint EFFECTS (OCC buckets via
 * PATCH /occ/lines/:id + the A1 acknowledge status flip) but has no endpoint
 * that stores the per-line 黃單 capture fields (交貨日/數量/價格調整/箱數/裝箱).
 * Until the S3.3 FulfillmentLine handlers add one, the capture record itself
 * is kept HERE (persisted to localStorage) so the three-checkpoint timeline
 * and audit trail survive a reload. Slice followUp: move server-side.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  PurchaseOrder,
  PurchaseOrderApprovalStatus,
  PurchaseOrderStatus,
} from '@/types/api/purchaseOrder';
import type { VendorCheckpointCode } from '@/constants/ofcVocabulary';

export type PoStatusFilter = 'all' | PurchaseOrderStatus;
export type PoApprovalFilter = 'all' | PurchaseOrderApprovalStatus;

export interface PoFilters {
  search: string;
  status: PoStatusFilter;
  approvalStatus: PoApprovalFilter;
  vendorIds: number[];
}

export interface PoPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** Exception kinds a vendor checkpoint can raise (short/over/delay). */
export type CheckpointExceptionKind = 'short' | 'over' | 'delay';

/** Owner routing per the SO→PO board: late→採購 · short-ship→船務 · unpaid→財務. */
export type CheckpointExceptionOwner = 'procurement' | 'shipping' | 'finance';

export interface CheckpointLineRecord {
  poLineId: string;
  sku: string;
  productName: string;
  /** Baseline the confirmation is judged against (PO qty; A3 uses A2 qty). */
  qtyBaseline: number;
  qtyConfirmed: number;
  /** 黃單 capture — 交貨日. */
  deliveryDate: string | null;
  /** 黃單 capture — 價格調整 flag. */
  priceAdjusted: boolean;
  /** 黃單 capture — 箱數. */
  boxCount: number | null;
  /** 黃單 capture — 裝箱 packaging note. */
  packaging: string | null;
  exception: {
    kind: CheckpointExceptionKind;
    owner: CheckpointExceptionOwner;
    /** short/over quantity gap (0 for pure delay). */
    gap: number;
  } | null;
}

export interface VendorCheckpointRecord {
  id: string;
  poId: string;
  poNumber: string;
  checkpoint: VendorCheckpointCode;
  at: string;
  note: string | null;
  lines: CheckpointLineRecord[];
}

export interface PoState {
  // Data (list echo — kept in sync by usePurchaseOrdersTable / mutations)
  purchaseOrders: PurchaseOrder[];

  // Pagination echo
  pagination: PoPagination;

  // Filters
  filters: PoFilters;

  // Vendor-checkpoint capture log (persisted client-side — see header)
  checkpointRecords: VendorCheckpointRecord[];

  // Actions - data
  setPurchaseOrders: (purchaseOrders: PurchaseOrder[]) => void;
  upsertPurchaseOrderInList: (purchaseOrder: PurchaseOrder) => void;

  // Actions - pagination
  setPagination: (pagination: Partial<PoPagination>) => void;

  // Actions - filters
  setSearch: (search: string) => void;
  setStatus: (status: PoStatusFilter) => void;
  setApprovalStatus: (approvalStatus: PoApprovalFilter) => void;
  setVendorIds: (vendorIds: number[]) => void;
  resetFilters: () => void;

  // Actions - checkpoint log
  appendCheckpointRecord: (record: VendorCheckpointRecord) => void;

  // Actions - reset
  reset: () => void;
}

const initialState = {
  purchaseOrders: [] as PurchaseOrder[],
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    status: 'all' as PoStatusFilter,
    approvalStatus: 'all' as PoApprovalFilter,
    vendorIds: [] as number[],
  },
  checkpointRecords: [] as VendorCheckpointRecord[],
};

export const usePoStore = create<PoState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setPurchaseOrders: (purchaseOrders) =>
          set({ purchaseOrders }, false, 'po/setPurchaseOrders'),

        upsertPurchaseOrderInList: (purchaseOrder) =>
          set(
            (state) => {
              const exists = state.purchaseOrders.some((po) => po.id === purchaseOrder.id);
              return {
                purchaseOrders: exists
                  ? state.purchaseOrders.map((po) =>
                      po.id === purchaseOrder.id ? purchaseOrder : po
                    )
                  : [purchaseOrder, ...state.purchaseOrders],
              };
            },
            false,
            'po/upsertPurchaseOrderInList'
          ),

        setPagination: (pagination) =>
          set(
            (state) => ({ pagination: { ...state.pagination, ...pagination } }),
            false,
            'po/setPagination'
          ),

        setSearch: (search) =>
          set(
            (state) => ({
              filters: { ...state.filters, search },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'po/setSearch'
          ),

        setStatus: (status) =>
          set(
            (state) => ({
              filters: { ...state.filters, status },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'po/setStatus'
          ),

        setApprovalStatus: (approvalStatus) =>
          set(
            (state) => ({
              filters: { ...state.filters, approvalStatus },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'po/setApprovalStatus'
          ),

        setVendorIds: (vendorIds) =>
          set(
            (state) => ({
              filters: { ...state.filters, vendorIds },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'po/setVendorIds'
          ),

        resetFilters: () =>
          set(
            {
              filters: { ...initialState.filters },
              pagination: { ...initialState.pagination },
            },
            false,
            'po/resetFilters'
          ),

        appendCheckpointRecord: (record) =>
          set(
            (state) => ({ checkpointRecords: [record, ...state.checkpointRecords] }),
            false,
            'po/appendCheckpointRecord'
          ),

        reset: () => set(initialState, false, 'po/reset'),
      }),
      {
        name: 'po-storage',
        partialize: (state) => ({
          filters: state.filters,
          pagination: {
            page: state.pagination.page,
            pageSize: state.pagination.pageSize,
          },
          checkpointRecords: state.checkpointRecords,
        }),
      }
    ),
    { name: 'PoStore' }
  )
);
